import { addDays, eachDayOfInterval } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { TFunction } from 'i18next';
import { pathOr } from 'ramda';

// eslint-disable-next-line import-x/no-cycle
import {
  AgentDaySettings,
  AutoScheduleOptions,
  AutoScheduleSelectedRange,
  DaySettings,
  SchedulingAction,
  SchedulingChartType,
  SchedulingState,
} from './types';
import { STATIC_ELEMENT_HEIGHT, STATIC_POINT_HEIGHT } from '../../constants';
import { ForecastType, GetSchedulesQueryVariables } from '../../graphql/components';
import { SortOrder, sortAgentsBy } from '../../types';
// eslint-disable-next-line import-x/no-cycle
import { formatScheduleElementLabel } from './utils/formatUtils';
import { positioner } from './utils/positioner';
import { startOfDayInZone } from '../../utils/date';
import { createEndOfDateRange } from '../../utils/dateRange';

// Scheduling Scene Config
export const REMOVE_ELEMENT_KEYS = [
  'Delete', // Other browsers
  'Backspace',
];

export const CHART_BOTTOM_MARGIN = 15;

// Chart Config
export const CHART_15_MIN_INTERVAL_WIDTH = 35;
export const CHART_HOUR_INTERVAL_WIDTH = CHART_15_MIN_INTERVAL_WIDTH * 4;
export const CHART_WIDTH = CHART_HOUR_INTERVAL_WIDTH * 24;
export const CHART_HEIGHT = 200;
export const CHART_PADDING = 30;
export const MIN_15_DROP_ELEMENT_WIDTH = 35;
export const SCHEDULED_STAFF_COLOR = '#00b4ff';
export const REQUIRED_STAFF_COLOR = '#9adaff';
export const NET_STAFF_COLOR = '#fcd497';
export const ZERO_VALUE_COLOR = '#a3a3a3';
export const CHART_BACKGROUND_COLOR = '#f7f8f9';
export const CHART_OUT_OF_RANGE_COLOR = '#e9eef1';
export const CHART_LINE_WIDTH = 2;
export const MIN_1 = 60000;
export const MIN_3 = MIN_1 * 3;
export const MIN_15 = 900000;
export const MIN_5 = 300000;
export const HALF_AN_HOUR = MIN_15 * 2;
export const ONE_HOUR = HALF_AN_HOUR * 2;
export const HOURS_24 = 86400000;
export const DEFAULT_Y_AXIS_TICKS = [0, 1, 2, 3, 4];
export const Y_AXIS_TICKS_COUNT = 5;
export const MAX_COUNT_OF_SELECTED_QUEUES = 10;
export const DATA_SERIES_ID = 'schedule-elements';
export const NOT_ASSIGNED_SERIES_ID = 'not-assigned-agents';
export const PRODUCTION_ELEMENTS_SERIES_ID = 'production-schedule-elements';

export const SCHEDULING_CHART_COLOR_PALETTE = [
  '#239ED8',
  '#EEA47F',
  '#2CAE66',
  '#9C56B0',
  '#9D9D9D',
  '#E02020',
  '#9CC4D5',
  '#B1634E',
  '#FEE715',
  '#212121',
];

// timezone offset in minutes
export const timezoneOffset = (date: Date) => date.getTimezoneOffset();

export const defaultGetSchedulesVariables: GetSchedulesQueryVariables = {
  queueIds: undefined,
  scheduleEndDateTime: '',
  scheduleStartDateTime: '',
  scheduleName: undefined,
};

export const defaultDaySettings: Omit<DaySettings, 'dayIndex'> = {
  agents: {},
  hideNonWorkingAgents: false,
};

export const defaultAgentDaySettings: Omit<AgentDaySettings, 'agentId'> = {
  remote: false,
};

export const initialSchedulingState: SchedulingState = {
  filter: {
    sortByFields: sortAgentsBy.name,
    sortOrder: SortOrder.ASCENDING,
  },
  selectedAgentIds: [],
  selectedAgentIdsSorted: [],
  visibleAgentIds: [],
  visibleSupervisedAgentIds: [],
  visibleAgentIdsInViewPort: {},
  agents: {},
  supervisedAgentsRecordObj: {},
  lockedAgentsIds: {},
  inactiveAgentIdsWithScheduleElement: [],
  initialQueuesStaffingData: {},
  isCalculatingWorkContributionData: false,
  isCalculatingWorkContributionOfProductionElements: false,
  isLoadingQueuesStaffingOfProductionElements: false,
  isLoadingEstimatedServiceLevel: false,
  usePrecalculatedQueuesStaffing: true,
  isLoadingForecast: false,
  isLoadingProductionScheduleElements: false,
  nearestSurroundingDaysElements: {
    nextDay: {},
    previousDay: {},
  },
  nearestSurroundingDaysProductionElements: {
    nextDay: {},
    previousDay: {},
  },
  notAssignedAgentIdsWithScheduleElement: [],
  queuesStaffingOfProductionElementsData: {},
  scheduleHistory: {
    positionInHistory: 0,
    schedulingStateHistory: [],
  },
  schedulingChartState: {
    dataPerQueue: {},
    plotBandsData: [],
    scheduleElementsContributionPerQueue: {},
    yAxisTicks: DEFAULT_Y_AXIS_TICKS,
  },
  usedScheduleElements: [],
  usedScheduleElementsWithinCurrentDay: [],
  clipboard: {
    isPasteScheduleActive: false,
    copiedScheduleElements: [],
    agentDaySettings: {},
  },
  chartState: {
    boost: {
      useGPUTranslations: true,
    },
    credits: {
      enabled: false,
    },
    chart: {
      spacingLeft: 0,
      spacingRight: 0,
      spacingTop: 0,
      spacingBottom: 0,
      marginBottom: CHART_BOTTOM_MARGIN,
      width: CHART_WIDTH,
      style: {
        fontFamily: 'Roboto, sans-serif',
      },
    },
    exporting: {
      enabled: false,
    },
    series: [
      {
        data: [],
        id: DATA_SERIES_ID,
        name: DATA_SERIES_ID,
        type: 'gantt',
        // TODO: enough for 200 agents * 7 elements per day, but should be increased to 4-5 K
        turboThreshold: 1500,
      },
      {
        allowPointSelect: false,
        data: [],
        dataLabels: {
          enabled: false,
        },
        dragDrop: {
          draggableX: false,
          draggableY: false,
        },
        enableMouseTracking: false,
        events: {},
        name: NOT_ASSIGNED_SERIES_ID,
        id: NOT_ASSIGNED_SERIES_ID,
        pointWidth: STATIC_POINT_HEIGHT - 2,
        type: 'gantt',
      },
      {
        allowPointSelect: false,
        data: [],
        dragDrop: {
          draggableX: false,
          draggableY: false,
        },
        events: {},
        id: PRODUCTION_ELEMENTS_SERIES_ID,
        name: PRODUCTION_ELEMENTS_SERIES_ID,
        pointWidth: STATIC_POINT_HEIGHT,
        type: 'gantt',
      },
    ],
    plotOptions: {
      gantt: {
        states: {
          select: {
            animation: {
              duration: 0,
            },
            color: null as unknown as string, // Disabling gantt default selected state color
          },
          inactive: {
            animation: {
              duration: 0,
            },
            opacity: 1,
          },
          normal: {
            animation: false,
          },
        },
        borderRadius: 2,
        pointWidth: STATIC_POINT_HEIGHT,
      },
      series: {
        boostThreshold: 1, // to always use boost module
        lineWidth: 60,
        enableMouseTracking: true,
        dragDrop: {
          draggableX: true,
          draggableY: true,
          dragMinY: 0,
          dragPrecisionX: MIN_5, // Snap to 5 minutes
          groupBy: 'selected', // Multi drag & drop by selected Point property (added by Highcharts)
        },
        dataLabels: {
          enabled: true,
          formatter: formatScheduleElementLabel,
          style: {
            fontSize: '12px',
            pointerEvents: 'none', // Content of label should not prevent user from interacting with element
          },
          defer: false,
          useHTML: true,
        },
        allowPointSelect: true,
        animation: {
          duration: 0,
        },
        point: {
          events: {
            click() {
              const activeElement = document.activeElement as HTMLSelectElement;
              if (activeElement && activeElement.disabled) {
                activeElement.blur(); // Fix element delete issue in Windows/FireFox WFM-1189
              }
            },
          },
        },
      },
    },
    yAxis: [
      {
        categories: [],
        id: 'people',
        min: 0,
        max: 0,
        staticScale: STATIC_ELEMENT_HEIGHT,
        grid: {
          enabled: false,
          cellHeight: STATIC_ELEMENT_HEIGHT,
        },
        title: {
          text: '',
        },
        labels: {
          enabled: false,
        },
      },
    ],
    xAxis: [
      {
        categories: [],
        id: 'time',
        grid: {
          enabled: false,
        },
        title: {
          text: '',
        },
        labels: {
          enabled: false,
        },
        currentDateIndicator: false,
        min: 0,
        max: 0,
        tickInterval: MIN_15,
        className: 'zoom-xAxis-minutes',
      },
    ],
    tooltip: {
      positioner,
      xDateFormat: '%a %b %d, %H:%M',
      backgroundColor: '#fff',
      borderWidth: 0,
      useHTML: true,
      padding: 0,
      shadow: false,
      shape: 'square',
      style: {
        fontSize: '14px',
      },
      followPointer: true,
      outside: true,
    },
  },
  forecast: {},
  forecastDataType: ForecastType.Production,
  schedulingChartType: SchedulingChartType.Production,
  missingForecastIntervalsPerQueue: {},
  missingOrIncompleteForecastQueueIds: [],
  productiveElementsIds: [],
  scheduleElements: [],
  daysSettings: {},
  days: [],
  daysInScheduleTimezone: [],
  pickedDayIndex: 0,
  isDirty: false,
  schedulingAction: SchedulingAction.INITIAL,
  pinnedScheduleElementsIds: [],
  pinnedUsedScheduleElementsIds: [],
  productionElementsWorkContributionData: {},
  productionScheduleElements: [],
  productionScheduleElementsCoverage: [],
  productionScheduleElementsMissingQueueIds: [],
  productionScheduleElementsWithinCurrentDay: [],
  queueIds: [],
  queues: {},
  selectedScheduleElementsIds: [],
  scheduleEndTime: 0, // will be replaced by valid value on init
  scheduleStartTime: 0, // will be replaced by valid value on init
  skills: {},
  timezone: '', // will be replaced by valid value on init
  viewedTimezone: '', // will be replaced by valid value on init
  workContributionData: {},
  autoScheduleOptions: {
    weightMaxUnderStaff: 10,
    weightBreakSpread: 0,
    weightChannelSpread: 8,
  },
  autoScheduleSelectedRange: {
    strictSelectedRange: false,
    startDateTime: 0, // will be replaced by valid value on init
    endDateTime: 0, // will be replaced by valid value on init
  },
};

export const getInitialSchedulingState =
  (
    t: TFunction,
    isDirty: boolean,
    timezone: string,
    lastViewedTimezone?: string,
    lastAutoScheduleSelectedRange?: AutoScheduleSelectedRange,
    lastAutoScheduleOptions?: AutoScheduleOptions,
    startDate = 0,
    endDate = 0
  ) =>
  (initialState: SchedulingState): SchedulingState => {
    const dayEndDate = addDays(startDate, 1);
    const viewedTimezone = lastViewedTimezone ?? timezone;
    const startDateLocal = utcToZonedTime(new Date(startDate), timezone);
    const endDateLocal = utcToZonedTime(new Date(endDate), timezone);

    const daysInRangeLocal = eachDayOfInterval({ start: startDateLocal, end: endDateLocal });
    const newDaysInScheduleTimezone = daysInRangeLocal.map((day) =>
      startOfDayInZone(utcToZonedTime(day.getTime(), timezone), timezone)
    );

    const minDateZoned = startOfDayInZone(new Date(startDate), viewedTimezone).getTime();
    const maxDateZoned = startOfDayInZone(new Date(dayEndDate), viewedTimezone).getTime();

    return {
      ...initialState,
      isDirty,
      timezone,
      viewedTimezone,
      days: daysInRangeLocal,
      daysInScheduleTimezone: newDaysInScheduleTimezone,
      chartState: {
        ...initialState.chartState,
        plotOptions: {
          ...initialState.chartState.plotOptions,
          series: {
            ...initialState.chartState.plotOptions!.series,
            dragDrop: {
              ...initialState.chartState.plotOptions!.series!.dragDrop,
              dragMaxX: createEndOfDateRange(endDate).getTime(), // wholeScheduleEndDateTime + 1 day for overnight shifts,
              dragMinX: startDate, // wholeScheduleStartDateTime,
            },
          },
        },
        xAxis: [
          {
            ...pathOr({}, ['chartState', 'xAxis', 0], initialState),
            max: maxDateZoned,
            min: minDateZoned,
          },
        ],
      },
      scheduleStartTime: startDate, // wholeScheduleStartDateTime,
      scheduleEndTime: endDate, // wholeScheduleEndDateTime,
      autoScheduleSelectedRange: lastAutoScheduleSelectedRange
        ? { ...lastAutoScheduleSelectedRange }
        : {
            strictSelectedRange: false,
            startDateTime: startDateLocal.getTime(),
            /**
             * Auto-schedule generation should not be triggered upon +1 extended day
             * (as the corresponding night shift would have overflowed to +2 day which is not allowed)
             */
            endDateTime: endDateLocal.getTime(),
          },
      autoScheduleOptions: lastAutoScheduleOptions ? { ...lastAutoScheduleOptions } : initialState.autoScheduleOptions,
    };
  };
